import { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from '@vd/ui';
import { Mic } from 'lucide-react';
import styles from './MicDevicePickerModal.module.css';

/**
 * Microphone device picker modal for dictation.
 *
 * **The hard truth**: the Web Speech API (`SpeechRecognition`) does
 * NOT accept a device id. Chrome's implementation picks "the default
 * input device" at recognition-start time, where "default" is whatever
 * combination of OS + Chrome site permission Chrome computes. There's
 * no JS API to say "use this mic."
 *
 * What this modal CAN do:
 *   1. Show the user the available mics so they know what's installed
 *      and what Chrome might pick from
 *   2. Live volume meter so the user can VERIFY their chosen mic is
 *      actually picking up sound (useful when Chrome silently swaps
 *      to the laptop mic and they don't realize)
 *   3. Save their preference to localStorage; MicDictation will then
 *      call getUserMedia({ deviceId: { exact: ... } }) before starting
 *      Web Speech, which MAY influence Chrome to use that device. No
 *      guarantee — depends on Chromium version and per-site settings.
 *   4. Provide the actual fix: a link to Chrome's site-settings page
 *      where the user can pin a specific mic for app.toado.dev.
 *
 * Future option: drop Web Speech in favor of a getUserMedia +
 * MediaRecorder + cloud transcription (Whisper/Deepgram) pipeline.
 * That'd give us TRUE device control, plus higher accuracy. Bigger
 * commit. See "Option B" in the planning notes.
 */

interface MicDevicePickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Currently-saved device id (or null for "use Chrome default"). */
  selectedDeviceId: string | null;
  /** Called when the user clicks Save with a final selection (or null
   *  if they cleared the choice). */
  onSelect: (deviceId: string | null) => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'needs-permission' }
  | { kind: 'permission-denied' }
  | { kind: 'ready'; devices: MediaDeviceInfo[] }
  | { kind: 'no-devices' };

// NOTE: `chrome://settings/...` URLs are blocked from anchor-tag
// navigation by Chrome since ~2018 — clicking them does nothing. So
// we surface human instructions instead of a link the user would
// click into a void. The lock-icon path works in every Chrome
// version and is browser-version-independent.

export function MicDevicePickerModal({
  open,
  onClose,
  selectedDeviceId,
  onSelect,
}: MicDevicePickerModalProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<string | null>(selectedDeviceId);

  // Reset pending whenever the modal reopens — the previously-saved
  // selection is the starting point each time. Without this, a user
  // who picks something then cancels gets stuck on their pre-cancel
  // choice on next open.
  useEffect(() => {
    if (open) setPending(selectedDeviceId);
  }, [open, selectedDeviceId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
        setLoad({ kind: 'no-devices' });
        return;
      }
      try {
        // Enumerate up front. Per the spec, labels are EMPTY until the
        // user has granted mic permission — so if we get back unlabeled
        // devices, we show the permission-request state.
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const audioInputs = devices.filter((d) => d.kind === 'audioinput');
        if (audioInputs.length === 0) {
          setLoad({ kind: 'no-devices' });
          return;
        }
        const haveLabels = audioInputs.some((d) => d.label && d.label.length > 0);
        if (!haveLabels) {
          setLoad({ kind: 'needs-permission' });
          return;
        }
        setLoad({ kind: 'ready', devices: audioInputs });
      } catch (err) {
        console.error('[mic-picker] enumerateDevices failed:', err);
        if (!cancelled) setLoad({ kind: 'no-devices' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // After the user grants permission, re-enumerate to get labeled devices.
  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      setLoad(
        audioInputs.length === 0
          ? { kind: 'no-devices' }
          : { kind: 'ready', devices: audioInputs },
      );
    } catch (err) {
      console.error('[mic-picker] permission request failed:', err);
      setLoad({ kind: 'permission-denied' });
    }
  };

  const handleSave = () => {
    onSelect(pending);
    onClose();
  };

  const handleClear = () => {
    onSelect(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} width={520} labelledBy="mic-picker-title">
      <ModalHeader id="mic-picker-title">Microphone</ModalHeader>
      <ModalBody>
        {load.kind === 'loading' && (
          <p className={styles.statusText}>Looking for microphones…</p>
        )}

        {load.kind === 'needs-permission' && (
          <div>
            <p className={styles.statusText}>
              To see your available microphones, allow Toado to access the mic. The
              permission prompt only fires once.
            </p>
            <Button variant="primary" size="sm" onClick={requestPermission}>
              Allow microphone access
            </Button>
          </div>
        )}

        {load.kind === 'permission-denied' && (
          <div>
            <p className={styles.statusText}>
              Microphone access was denied. If you blocked it by mistake,
              click <strong>Try again</strong>. If Chrome won't re-prompt,
              click the <strong>lock icon</strong> next to the URL →{' '}
              <strong>Site settings</strong> → set Microphone to{' '}
              <strong>Allow</strong>, then come back here and retry.
            </p>
            <Button variant="primary" size="sm" onClick={requestPermission}>
              Try again
            </Button>
          </div>
        )}

        {load.kind === 'no-devices' && (
          <p className={styles.statusText}>
            No microphones detected. Plug one in, or check your system
            audio settings.
          </p>
        )}

        {load.kind === 'ready' && (
          <>
            <div className={styles.deviceList}>
              {load.devices.map((device) => {
                const id = device.deviceId;
                const checked = pending === id;
                return (
                  <label key={id} className={styles.deviceRow}>
                    <input
                      type="radio"
                      name="mic-device"
                      checked={checked}
                      onChange={() => setPending(id)}
                    />
                    <div className={styles.deviceMeta}>
                      <span className={styles.deviceLabel}>
                        {device.label || '(unnamed microphone)'}
                      </span>
                      {id === 'default' && (
                        <span className={styles.deviceHint}>System default</span>
                      )}
                    </div>
                    {checked && <VolumeMeter deviceId={id} />}
                  </label>
                );
              })}
            </div>
            <p className={styles.note}>
              <Mic size={11} aria-hidden="true" />
              <span>
                Speech recognition may still use Chrome's default mic
                regardless of your selection here. To force a specific mic for
                this site: click the <strong>lock icon</strong> next to the URL
                → <strong>Site settings</strong> → <strong>Microphone</strong>{' '}
                → pick your mic.
              </span>
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {selectedDeviceId !== null && (
          <Button variant="ghost" onClick={handleClear}>
            Clear preference
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={load.kind !== 'ready'}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Live volume meter for the currently-selected device. Acquires its
 * own MediaStream + AudioContext for the duration of the modal. The
 * stream is closed cleanly on unmount (which happens when the user
 * picks a different device — React unmounts this when `key={deviceId}`
 * effectively changes).
 *
 * The fill width is updated on RAF; AudioContext FFT samples are
 * cheap. No setState churn — DOM updates directly via a ref to keep
 * React out of the per-frame path.
 */
function VolumeMeter({ deviceId }: { deviceId: string }) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let stopped = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // Cast through unknown so older TS lib doesn't whinge about
        // webkit-prefixed fallback we don't actually need.
        const Ctx = window.AudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const buffer = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (stopped) return;
          analyser.getByteFrequencyData(buffer);
          // Average normalized to 0..1
          let sum = 0;
          for (let i = 0; i < buffer.length; i++) sum += buffer[i] ?? 0;
          const avg = sum / buffer.length / 255;
          // Visual response is more sensible with a square-root scale
          // because most everyday speech sits in the lower amplitude
          // range; linear scaling makes the bar look almost-empty for
          // normal speech.
          const pct = Math.min(100, Math.sqrt(avg) * 140);
          if (barRef.current) barRef.current.style.width = `${pct}%`;
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        console.warn('[mic-picker] meter setup failed:', err);
      }
    })();

    return () => {
      stopped = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (audioCtxRef.current) {
        // close() is async; don't await — we just want to release the
        // hardware reference. If it errors (already-closed contexts in
        // strict-mode double mount), swallow.
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, [deviceId]);

  return (
    <div className={styles.meter} aria-hidden="true">
      <div ref={barRef} className={styles.meterFill} />
    </div>
  );
}
